import '.a{display:flex;}.ssr-plugin'
import '.ae{justify-content:space-between;}.ssr-plugin'
import '.b8{gap:8px;}.ssr-plugin'
import '.d{align-items:center;}.ssr-plugin'
import { FC, memo, ReactNode } from 'react'
import { styled, Style } from 'inlines'
import { ThemeColors, useTheme } from '../providers/useTheme'
import { Text } from './Text'
import { capitalize } from '../utils/capitalize'
import { Icon, IconProps } from './Icon'

type VisualVariant = 'filled' | 'shaded' | 'ghost'

export type ButtonProps = {
  id?: string
  children?: ReactNode
  label?: string
  leadIcon?: IconProps['variant']
  trailIcon?: IconProps['variant']
  color?: string | ThemeColors
  background?: string | ThemeColors
  isHovered?: boolean
  type?: 'button' | 'submit'
  isDisabled?: boolean
  isRaw?: boolean
  variant?: VisualVariant
  'aria-label'?: string
  style?: Style
  on?: {
    click?: () => void
  }
}

const baseStyle: Style = {
  display: 'inline-flex',
  height: 48,
  padding: '12px 16px',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 4,
  flexShrink: 0,
  borderRadius: 999,
  border: 'none',
  cursor: 'pointer',
  outlineOffset: 0,
}

const getVariantStyle = (
  variant: VisualVariant,
  isHovered: boolean,
  isDisabled: boolean,
  primaryAccent: string,
  background: string,
  outline: string,
): Style => {
  if (variant === 'filled') {
    if (isDisabled) {
      return {
        background: primaryAccent,
        opacity: 0.3,
        cursor: 'not-allowed',
      }
    }

    if (isHovered) {
      return {
        background: `linear-gradient(
          0deg,
          rgba(255, 255, 255, 0.15) 0%,
          rgba(255, 255, 255, 0.15) 100%
        ), ${primaryAccent}`,
        opacity: 1,
      }
    }

    return {
      background: primaryAccent,
      opacity: 1,
    }
  }

  if (isDisabled) {
    return {
      background: `linear-gradient(
        0deg,
        rgba(0, 0, 0, 0.15) 0%,
        rgba(0, 0, 0, 0.15) 100%
      ), ${background}`,
      opacity: 0.3,
      cursor: 'not-allowed',
    }
  }

  if (isHovered) {
    return {
      background: `linear-gradient(
        0deg,
        rgba(0, 0, 0, 0.30) 0%,
        rgba(0, 0, 0, 0.30) 100%
      ), ${background}`,
      opacity: 1,
    }
  }

  return {
    background: `linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.15) 0%,
      rgba(0, 0, 0, 0.15) 100%
    ), ${background}`,
    opacity: 1,
  }
}

export const Button: FC<ButtonProps> = memo(
  ({
    id = 'button',
    children,
    label = '',
    leadIcon,
    trailIcon,
    isHovered = false,
    isDisabled = false,
    isRaw = false,
    color,
    background,
    type = 'button',
    variant = 'filled',
    on,
    'aria-label': ariaLabelProp,
    style,
    ...rest
  }) => {
    const { colors } = useTheme()

    if (!label && children !== undefined && typeof children === 'string') {
      label = children
    }

    const accessibleLabel = ariaLabelProp ?? label

    const finalStyle: Style = {
      ...baseStyle,
      ...getVariantStyle(
        variant,
        isHovered,
        isDisabled,
        (background as string) ?? colors.primaryAccent,
        colors.background,
        colors.outline,
      ),
      ...style,
    }

    const rawStyle: Style = {
      padding: 0,
      margin: 0,
      background: 'none',
      border: 'none',
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      cursor: 'pointer',
      ...style,
    }

    const contentColor = color
      ? color
      : variant === 'filled'
        ? colors.background
        : colors.textPrimary

    return (
      <styled.button
        id={id}
        type={type}
        disabled={isDisabled}
        aria-disabled={isDisabled || undefined}
        aria-label={accessibleLabel || undefined}
        style={isRaw ? rawStyle : finalStyle}
        onClick={on?.click}
        {...rest}
      >
        <div className="a ae b8 d"
        >
          {leadIcon && (
            <Icon variant={leadIcon} aria-hidden={true} color={contentColor} />
          )}

          {children
            ? children
            : label && (
                <Text
                  isNotSelectable
                  color={contentColor}
                  variant="subheading-medium"
                >
                  {capitalize(label)}
                </Text>
              )}

          {trailIcon && (
            <Icon variant={trailIcon} aria-hidden={true} color={contentColor} />
          )}
        </div>
      </styled.button>
    )
  },
)

Button.displayName = 'Button'
